import {
  AgreeToTermsPolicy,
  SignInIdentifier,
  VerificationType,
  type VerificationCodeIdentifier,
} from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { InputField, PasswordInputField } from '@/components/InputFields';
import TextLink from '@/components/TextLink';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useRegisterFlowCodeVerification from '@/containers/VerificationCode/use-register-flow-code-verification';
import useResendVerificationCode from '@/containers/VerificationCode/use-resend-verification-code';
import useCheckSingleSignOn from '@/hooks/use-check-single-sign-on';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import VerificationCodeInput, { defaultLength } from '@/shared/components/VerificationCode';
import { UserFlow } from '@/types';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import styles from './index.module.scss';

type Props = {
  readonly className?: string;
};

type FormState = {
  email: string;
  password: string;
  confirmPassword: string;
};

const InlineEmailRegisterForm = ({ className }: Props) => {
  const { t } = useTranslation();
  const { termsValidation, termsAgreement, isTermsDisabled, agreeToTermsPolicy } = useTerms();
  const { ssoConnectors } = useSieMethods();
  const { onSubmit: checkSingleSignOn } = useCheckSingleSignOn();
  const { setIdentifierInputValue, verificationIdsMap } = useContext(UserInteractionContext);

  const [emailToVerify, setEmailToVerify] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [codeInput, setCodeInput] = useState<string[]>([]);
  const [codeInputError, setCodeInputError] = useState<string>();
  const [passwordPolicyError, setPasswordPolicyError] = useState<string>();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  const password = watch('password');
  const verificationIdentifier = useMemo<VerificationCodeIdentifier>(
    () => ({ type: SignInIdentifier.Email, value: emailToVerify }),
    [emailToVerify]
  );
  const verificationId = verificationIdsMap[VerificationType.EmailVerificationCode] ?? '';
  const requiresTermsAgreement =
    !isTermsDisabled && agreeToTermsPolicy !== AgreeToTermsPolicy.Automatic;

  const checkPassword = usePasswordPolicyChecker({
    setErrorMessage: setPasswordPolicyError,
  });

  const handleCodeSent = useCallback(() => {
    setIsCodeSent(true);
    setCodeInput([]);
    setCodeInputError(undefined);
  }, []);

  const {
    errorMessage: sendErrorMessage,
    clearErrorMessage: clearSendErrorMessage,
    onSubmit: sendVerificationCode,
  } = useSendVerificationCode(UserFlow.Register, false, {
    onVerificationCodeSent: handleCodeSent,
  });

  const resetCodeInput = useCallback(() => {
    setCodeInput([]);
    setCodeInputError(undefined);
  }, []);

  const {
    errorMessage: verificationErrorMessage,
    clearErrorMessage: clearVerificationErrorMessage,
    onSubmit: verifyCode,
  } = useRegisterFlowCodeVerification(
    verificationIdentifier,
    verificationId,
    resetCodeInput,
    password
  );

  const { seconds, isRunning, onResendVerificationCode } = useResendVerificationCode(
    UserFlow.Register,
    verificationIdentifier
  );

  const isCodeInputReady = useMemo(
    () => codeInput.length === defaultLength && codeInput.every(Boolean),
    [codeInput]
  );

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearSendErrorMessage();
      setPasswordPolicyError(undefined);

      await handleSubmit(async ({ email, password: submittedPassword }) => {
        if (!(await termsValidation())) {
          return;
        }

        if (ssoConnectors.length > 0 && (await checkSingleSignOn(email))) {
          return;
        }

        if (!(await checkPassword(submittedPassword))) {
          return;
        }

        setEmailToVerify(email);
        setIdentifierInputValue({ type: SignInIdentifier.Email, value: email });
        await sendVerificationCode({
          identifier: SignInIdentifier.Email,
          value: email,
        });
      })(event);
    },
    [
      checkPassword,
      checkSingleSignOn,
      clearSendErrorMessage,
      handleSubmit,
      sendVerificationCode,
      setIdentifierInputValue,
      ssoConnectors.length,
      termsValidation,
    ]
  );

  const onVerifyCode = useCallback(async () => {
    if (requiresTermsAgreement && !termsAgreement) {
      return;
    }

    if (!isCodeInputReady) {
      setCodeInputError(t('error.invalid_passcode'));
      return;
    }

    setCodeInputError(undefined);
    clearVerificationErrorMessage();
    await verifyCode(codeInput.join(''));
  }, [
    clearVerificationErrorMessage,
    codeInput,
    isCodeInputReady,
    requiresTermsAgreement,
    t,
    termsAgreement,
    verifyCode,
  ]);

  return (
    <form className={classNames(styles.form, className)} onSubmit={onSubmitHandler}>
      <InputField
        className={styles.inputField}
        type="email"
        autoComplete="email"
        label={t('input.email')}
        disabled={isCodeSent}
        isDanger={!!errors.email || !!sendErrorMessage}
        errorMessage={errors.email?.message}
        {...register('email', {
          required: getGeneralIdentifierErrorMessage([SignInIdentifier.Email], 'required'),
          validate: (value) => {
            const error = validateIdentifierField(SignInIdentifier.Email, value);
            return error
              ? getGeneralIdentifierErrorMessage([SignInIdentifier.Email], 'invalid')
              : true;
          },
        })}
      />

      <PasswordInputField
        className={styles.inputField}
        autoComplete="new-password"
        label={t('input.password')}
        disabled={isCodeSent}
        isDanger={!!errors.password || !!passwordPolicyError}
        errorMessage={errors.password?.message ?? passwordPolicyError}
        {...register('password', { required: t('error.password_required') })}
      />

      <PasswordInputField
        className={styles.inputField}
        autoComplete="new-password"
        label={t('input.confirm_password')}
        disabled={isCodeSent}
        isDanger={!!errors.confirmPassword}
        errorMessage={errors.confirmPassword?.message}
        {...register('confirmPassword', {
          required: t('error.password_required'),
          validate: (value) => value === password || t('error.passwords_do_not_match'),
        })}
      />

      <TermsAndPrivacyCheckbox className={styles.terms} />

      {sendErrorMessage && (
        <ErrorMessage className={styles.formErrors}>{sendErrorMessage}</ErrorMessage>
      )}

      {!isCodeSent && (
        <Button
          title="action.create_account"
          htmlType="submit"
          isDisabled={requiresTermsAgreement && !termsAgreement}
          isLoading={isSubmitting}
        />
      )}

      {isCodeSent && (
        <div className={styles.verification}>
          <div className={styles.verificationDescription}>
            {t('description.enter_passcode', {
              address: t('description.email'),
              target: emailToVerify,
            })}
          </div>
          <VerificationCodeInput
            name="register_passcode"
            className={styles.verificationInput}
            value={codeInput}
            error={codeInputError ?? verificationErrorMessage}
            onChange={setCodeInput}
          />
          <div className={styles.resendMessage}>
            {isRunning ? (
              <Trans components={{ span: <span key="counter" /> }}>
                {t('description.resend_after_seconds', { seconds })}
              </Trans>
            ) : (
              <Trans
                components={{
                  a: (
                    <TextLink
                      onClick={async () => {
                        clearVerificationErrorMessage();
                        await onResendVerificationCode();
                        resetCodeInput();
                      }}
                    />
                  ),
                }}
              >
                {t('description.resend_passcode')}
              </Trans>
            )}
          </div>
          <Button
            title="action.create_account"
            htmlType="button"
            isDisabled={requiresTermsAgreement && !termsAgreement}
            onClick={onVerifyCode}
          />
        </div>
      )}
    </form>
  );
};

export default InlineEmailRegisterForm;
